/**
 * Блок сводного остатка банковских счетов (карточки BL-2026-09-29-beba,
 * BL-2026-08-15-2024 §7.2).
 *
 * Flow: опрос /api/treasury/bank-balances раз в 60 секунд → «Собственные
 *       средства в банках» (долларовый итог либо «итога нет» с причинами) →
 *       отдельной строкой клиентские средства на банковских счетах → свод по
 *       валютам → «данные на» самый старый срез выписки.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadBankBalances;
 *   - ./PspTotalsPanel: formatUsdTotal, formatRateMoment — тот же вид
 *     величин, что у соседнего блока PSP;
 *   - ./BankStatementCommon: formatAmount, formatCut.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок в разделе PSP/Bank;
 *   - src/PspTotalsPanel.jsx — образец устройства блока;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §3.3, §6.9.
 *
 * # ДВЕ ВЕЛИЧИНЫ, А НЕ ОДНА
 *
 * Клиентские деньги на сегрегированных счетах SBM — актив, обеспечивающий
 * обязательство перед клиентами, а не ликвидность компании (решение
 * стейкхолдера 2026-10-14). Сложенные с собственными в одну сумму, они
 * завысили бы «сколько денег у компании». Поэтому величины две, и вторая
 * подписана прямо: «не ликвидность компании».
 *
 * # «ИТОГА НЕТ» — НЕ НОЛЬ
 *
 * `own_total_usd = null` значит, что хотя бы одна валюта группы без курса
 * (BR-010: частичная сумма итогом не выдаётся). На месте суммы тогда слова
 * и причины, а рублёвая величина видна в своде по валютам ниже.
 *
 * # ПОКА БАНКОВ НЕТ
 *
 * Ни одного банковского источника (`sources` пуст) — блок остаётся на месте
 * и прямо говорит, что выписок нет: молчание о банковской стороне читалось
 * бы как «банковских денег нет» (довод первой редакции блока).
 */

import { useCallback, useEffect, useState } from "react";

import { loadBankBalances } from "./api";
import {
  WORD_FORMS,
  countWord,
  formatAmount,
  formatCut,
  useLatestRequest,
} from "./BankStatementCommon";
import { formatRateMoment, formatUsdTotal } from "./PspTotalsPanel";

/** Период опроса — как у остальных блоков платёжного контура. */
const POLL_MS = 60000;

/**
 * Долларовый итог группы либо «итога нет».
 *
 * @param {string|null} value — итог в USD или null
 */
function UsdTotal({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="state-unknown">итога нет</span>;
  }
  return (
    <>
      {formatUsdTotal(value)} <span className="unit">USD</span>
    </>
  );
}

export default function BankTotalsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Опрос раз в минуту: медленный ответ не должен перетереть следующий,
  // а ответ после ухода из раздела — применяться к снятому блоку.
  const begin = useLatestRequest();

  const load = useCallback(() => {
    const isCurrent = begin();
    loadBankBalances()
      .then((d) => {
        if (!isCurrent()) return;
        setData(d);
        setError("");
      })
      .catch((e) => {
        if (isCurrent()) setError(e.message);
      });
  }, [begin]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (error && !data) {
    return (
      <section className="card">
        <h2>Общий баланс Bank</h2>
        <div className="error">{error}</div>
      </section>
    );
  }

  if (!data) {
    return (
      <section className="card">
        <h2>Общий баланс Bank</h2>
        <p className="muted">Загрузка…</p>
      </section>
    );
  }

  if (!data.sources || data.sources.length === 0) {
    // Здесь НЕТ ни нуля, ни прочерка в позиции суммы: место величины занято
    // словами о том, почему величины нет.
    return (
      <section className="card card-pending">
        <h2>Общий баланс Bank</h2>
        <p className="muted metric-note">
          Ни одной банковской выписки ещё не загружено — остаток по счетам
          система не видит. Это не ноль: выписки загружаются в разделе
          «Банковские выписки».
        </p>
      </section>
    );
  }

  const totals = data.totals || [];
  const withoutStatement = totals.reduce(
    (n, t) => n + (t.accounts_without_statement || 0),
    0,
  );
  const unknownAccounts = totals.reduce((n, t) => n + (t.accounts_unknown || 0), 0);

  return (
    <section className="card">
      <h2>Общий баланс Bank</h2>
      {error && <p className="error">Не обновилось: {error}</p>}

      <h3>Собственные средства в банках</h3>
      <p className="metric-big">
        <UsdTotal value={data.own_total_usd} />
      </p>

      <p className="bs-client-line">
        Клиентские средства на банковских счетах{" "}
        <span className="muted">(не ликвидность компании)</span>:{" "}
        <strong>
          <UsdTotal value={data.client_total_usd} />
        </strong>
      </p>

      <p className="muted metric-note">
        {data.oldest_cut_at && (
          <>Данные на {formatCut(data.oldest_cut_at, "day")} — самая старая из последних выписок. </>
        )}
        {data.rate_as_of && <>Курсы на {formatRateMoment(data.rate_as_of)}.</>}
      </p>

      {data.total_usd_complete === false && (
        <div className="incomplete">
          <strong className="warn">Долларовый итог неполный.</strong>
          <ul>
            {(data.total_usd_reasons || []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      {(withoutStatement > 0 || unknownAccounts > 0) && (
        <p className="warn cell-note">
          {withoutStatement > 0 &&
            `Без выписки: ${countWord(withoutStatement, WORD_FORMS.account)}. `}
          {unknownAccounts > 0 &&
            `Без разметки принадлежности: ${countWord(unknownAccounts, WORD_FORMS.account)} — в итоги не вошли.`}
        </p>
      )}

      <h3>По валютам</h3>
      {totals.length === 0 ? (
        <p className="muted">Остатков ещё нет.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Валюта</th>
              <th className="num">Собственные</th>
              <th className="num">Клиентские</th>
            </tr>
          </thead>
          <tbody>
            {totals.map((t) => (
              <tr key={t.currency}>
                <td title={`счетов: собственных ${t.accounts_own}, клиентских ${t.accounts_client}`}>
                  {t.currency}
                  {t.accounts_unknown > 0 && (
                    <div className="warn cell-note">
                      не размечено {formatAmount(t.unknown)}
                    </div>
                  )}
                </td>
                <td className="num">{formatAmount(t.own)}</td>
                <td className="num">{formatAmount(t.client)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
